import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, makeStyles, TextField, Typography } from '@material-ui/core';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../../auth';
import { getBlogPublicProfile, getBlogSessionToken, initializeBlogPublicProfile, saveBlogPublicProfile } from '../../../Apis/touzenBlogApi';
import { useBlogReleasePolicy } from './BlogReleasePolicy';

const useStyles = makeStyles((theme) => ({
  section: { margin: theme.spacing(4, 0), padding: theme.spacing(3, 0), borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}`, overflowWrap: 'anywhere', scrollMarginTop: '128px', textAlign: 'left' },
  heading: { fontSize: '1.125rem', lineHeight: 1.5, marginBottom: theme.spacing(2) },
  profileRow: {
    display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '88px',
    margin: '72px 0', textAlign: 'left', overflowWrap: 'anywhere', scrollMarginTop: '128px',
    '@media screen and (max-width:750px)': { gridTemplateColumns: 'minmax(0, 1fr)', gap: '24px' },
  },
  profileIntro: { minWidth: 0 },
  profileTitle: { color: theme.palette.primary.main, fontWeight: 'bold', fontSize: 20, lineHeight: 1.5 },
  profileLead: { fontSize: 16, lineHeight: 1.7, marginTop: theme.spacing(2) },
  profileControls: { minWidth: 0 },
  saveButton: {
    display: 'inline-block', fontFamily: theme.typography.fontFamily, fontWeight: 'bold',
    borderRadius: '0.9em', border: 'none', padding: '8px 12px',
    backgroundColor: 'rgba(192,192,192, 1)', boxShadow: '0px 0px 15px rgba(0,0,0,0.1)', color: '#000', cursor: 'pointer',
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    '&:disabled': { opacity: 0.5, cursor: 'default' },
  },
  field: {
    width: '100%', maxWidth: 560, margin: theme.spacing(2, 0),
    '& .MuiInputBase-root': { fontFamily: theme.typography.fontFamily, fontSize: 16, minHeight: 44, borderRadius: 8, boxShadow: 'inset 0 0 15px rgba(0,0,0,0.1)' },
    '& .MuiInputLabel-root': { fontSize: 14, fontFamily: theme.typography.fontFamily },
    '& .MuiFormHelperText-root': { fontSize: '0.875rem', lineHeight: 1.7 },
    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(1)' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#999', borderWidth: '0.5px' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
  },
  actions: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1), alignItems: 'center', '& a, & button': { minHeight: 44, fontSize: '0.875rem' }, '& a:focus-visible, & button:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } },
  shareRow: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(2), minWidth: 0,
    '& $field': { flex: 1, minWidth: 0, maxWidth: 'none' },
    '& $actions': { flexShrink: 0 },
    '& $actions .MuiButton-root.MuiButtonBase-root': { minHeight: 44 },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column', alignItems: 'stretch', gap: 0,
      '& $field': { flex: 'none' },
      '& $actions': { alignSelf: 'flex-start' },
    },
  },
  readonlyName: {
    '&& .MuiInputBase-root.MuiOutlinedInput-root': { backgroundColor: theme.palette.action.hover, boxShadow: 'none', cursor: 'not-allowed' },
    '&& input': { cursor: 'not-allowed' },
    '&& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline, && .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, && .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider, borderWidth: '1px' },
  },
  message: { fontSize: '0.875rem', lineHeight: 1.7, margin: theme.spacing(1, 0) },
}));

const accountScope = (auth) => `${Boolean(auth?.isAuth)}:${auth?.user?.id || auth?.user?.uuid || auth?.user?.email || ''}`;
export const publicNameErrorMessage = (error, saving = false) => {
  if (error?.kind === 'forbidden') return '公開名を変更する資格がありません。';
  if (error?.kind === 'unauthenticated' || error?.kind === 'session_changed') return saving
    ? 'ログイン状態を確認できません。保存結果を確認してから再操作してください。' : 'ログイン状態を確認してください。';
  if (error?.kind === 'validation') return '公開名を1〜255文字で入力してください。';
  return saving ? '保存結果を確認できませんでした。公開名を再読み込みして確認してから再操作してください。' : '公開名を読み込めませんでした。';
};

// The profile is independent of article state: focus refresh never resets the editor.
export function usePublicNameProfile({ refreshOnFocus = false, initializeDefaults = false, enabled = true } = {}) {
  const auth = useContext(AuthContext) || {};
  const scope = accountScope(auth);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const enabledRef = useRef(enabled); enabledRef.current = enabled;
  const controller = useRef(null);
  const generation = useRef(0);
  const [state, setState] = useState({ scope, profile: null, loading: Boolean(auth.isAuth), error: null, initializing: false, initializationFailed: false });
  const acceptProfile = useCallback((profile) => {
    generation.current += 1;
    controller.current?.abort();
    setState({ scope, profile, loading: false, error: null, initializing: false, initializationFailed: false });
  }, [scope]);
  const reload = useCallback(async () => {
    controller.current?.abort();
    const requestController = new AbortController();
    controller.current = requestController;
    const requestGeneration = ++generation.current;
    // Pin the read identity before the optional initialization write, even before AuthContext updates.
    const token = auth.isAuth && initializeDefaults ? getBlogSessionToken() : '';
    const currentRequest = () => enabledRef.current && !requestController.signal.aborted && scopeRef.current === scope && generation.current === requestGeneration;
    if (!enabled) return;
    if (!auth.isAuth) { setState({ scope, profile: null, loading: false, error: null, initializing: false, initializationFailed: false }); return; }
    let initializationStarted = false;
    setState((current) => ({ scope, profile: current.scope === scope ? current.profile : null, loading: true, error: null, initializing: false, initializationFailed: false }));
    try {
      let profile = await getBlogPublicProfile(requestController.signal);
      if (!currentRequest()) return;
      if (initializeDefaults && getBlogSessionToken() !== token) throw Object.assign(new Error('TOUZEN_PUBLIC_NAME_SESSION_CHANGED'), { kind: 'session_changed' });
      if (initializeDefaults && profile.can_manage_blog && profile.author_key === null) {
        initializationStarted = true;
        setState({ scope, profile, loading: true, error: null, initializing: true, initializationFailed: false });
        profile = await initializeBlogPublicProfile(requestController.signal, token);
        if (!currentRequest()) return;
        if (getBlogSessionToken() !== token) throw Object.assign(new Error('TOUZEN_PUBLIC_NAME_SESSION_CHANGED'), { kind: 'session_changed' });
      }
      setState({ scope, profile, loading: false, error: null, initializing: false, initializationFailed: false });
    } catch (error) {
      if (currentRequest()) setState((current) => ({ ...current, loading: false, error, initializing: false, initializationFailed: initializationStarted }));
    }
  }, [scope, auth.isAuth, initializeDefaults, enabled]);
  useEffect(() => {
    reload();
    return () => { generation.current += 1; controller.current?.abort(); };
  }, [reload]);
  useEffect(() => {
    if (!enabled || !refreshOnFocus || !auth.isAuth) return undefined;
    const refresh = () => { reload(); };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [refreshOnFocus, auth.isAuth, reload, enabled]);
  const current = state.scope === scope ? state : { profile: null, loading: Boolean(auth.isAuth), error: null };
  return { ...current, reload, acceptProfile };
}

export function PublicNameSettingsLink({ children = '公開名を設定する' }) {
  const location = useLocation();
  const domain = new URLSearchParams(location.search).get('domain') === 'touzen' ? '?domain=touzen' : '';
  return <Button component={Link} role="link" to={`/mypage${domain}#public-name`} target="_blank" rel="noopener noreferrer">{children}（別タブ）</Button>;
}

export function BlogAuthorName({ name, own, loading, error, onRetry }) {
  const classes = useStyles();
  return <div>
    <TextField id="blog-author-public-name" className={`${classes.field} ${classes.readonlyName}`} label="公開する投稿者名" value={name || ''} variant="outlined" size="small" fullWidth
      InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }}
      helperText={name ? own ? '本人のアカウント共通の公開名です。変更はMyPageで行ってください。' : '記事所有者のアカウント共通の公開名です。'
        : loading ? '公開名を確認しています。' : error ? '公開名を確認できませんでした。' : own ? '公開名は未登録です。MyPageで設定してから公開してください。' : '記事所有者の公開名は未登録です。公開前に所有者による設定が必要です。'} />
    {own && error && <Typography role="alert" className={classes.message}>{publicNameErrorMessage(error)}</Typography>}
    {own && <div className={classes.actions}><PublicNameSettingsLink />{error && <Button type="button" onClick={onRetry} disabled={loading || !onRetry}>公開名を再読み込み</Button>}</div>}
  </div>;
}

export function OwnBlogShare({ profile, loading, error, onRetry, className = '' }) {
  const classes = useStyles();
  const location = useLocation();
  const [copyMessage, setCopyMessage] = useState('');
  const mounted = useRef(false);
  const copying = useRef(false);
  const key = profile?.author_key;
  // Commit the lifetime guard and reset before the copy controls accept input.
  useLayoutEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useLayoutEffect(() => { setCopyMessage(''); }, [key]);
  const query = new URLSearchParams({ author: key || '' });
  if (new URLSearchParams(location.search).get('domain') === 'touzen') query.set('domain', 'touzen');
  const url = key ? `${window.location.origin}/blog?${query}` : '';
  const copy = async () => {
    if (copying.current) return;
    if (!navigator.clipboard?.writeText) { setCopyMessage('URLを選択して手動でコピーしてください。'); return; }
    copying.current = true;
    try {
      await navigator.clipboard.writeText(url);
      if (mounted.current) setCopyMessage('URLをコピーしました。');
    } catch (error) { if (mounted.current) setCopyMessage('URLを選択して手動でコピーしてください。'); }
    finally { copying.current = false; }
  };
  return <section className={`${classes.section} ${className}`.trim()} aria-label="自分の記事の共有URL">
    <Typography component="h2" className={classes.heading}>自分の記事の共有URL</Typography>
    <Typography className={classes.message}>自分の公開記事だけを表示します。</Typography>
    {loading && !profile && <Typography role="status" className={classes.message}>公開名を確認しています。</Typography>}
    {error && <div role="alert"><Typography className={classes.message}>{publicNameErrorMessage(error)}</Typography><Button onClick={onRetry}>公開名を再読み込み</Button></div>}
    {url ? <>
      <div className={classes.shareRow}>
        <TextField id="blog-own-share-url" className={classes.field} label="共有URL" value={url} fullWidth variant="outlined" size="small" InputLabelProps={{ shrink: true }}
          InputProps={{ readOnly: true }} onFocus={(event) => event.target.select()} />
        <div className={classes.actions}><Button type="button" variant="outlined" onClick={copy}>URLをコピー</Button></div>
      </div>
      {copyMessage && <Typography role="status" className={classes.message}>{copyMessage}</Typography>}
    </> : !loading && !error && <div className={classes.actions}><PublicNameSettingsLink>公開名を登録して共有URLを作る</PublicNameSettingsLink></div>}
  </section>;
}

function PublicNameForm({ layoutReady, enabled }) {
  const classes = useStyles();
  const location = useLocation();
  const { profile, loading, error, reload, acceptProfile, initializing, initializationFailed } = usePublicNameProfile({ initializeDefaults: true, enabled });
  const [draftName, setDraftName] = useState(null);
  const name = draftName === null ? profile?.display_name || '' : draftName;
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [saveError, setSaveError] = useState(null);
  const controller = useRef(null);
  const sending = useRef(false);
  const section = useRef(null);
  const scrolled = useRef(false);
  useEffect(() => () => controller.current?.abort(), []);
  useEffect(() => {
    if (enabled && layoutReady && !scrolled.current && location.hash === '#public-name') {
      scrolled.current = true;
      section.current?.scrollIntoView?.({ block: 'start' });
    }
  }, [enabled, layoutReady, location.hash]);
  const save = async (event) => {
    event.preventDefault();
    if (!enabled || sending.current || loading || initializationFailed || !profile?.can_edit_public_name) return;
    setMessage(''); setSaveError(null);
    if (!name.trim() || Array.from(name.trim()).length > 255) { setSaveError({ kind: 'validation' }); return; }
    sending.current = true; setSaving(true);
    const requestController = new AbortController();
    controller.current = requestController;
    try {
      const saved = await saveBlogPublicProfile(name, requestController.signal);
      if (requestController.signal.aborted) return;
      acceptProfile(saved); setDraftName(null); setMessage('公開名を保存しました。');
    } catch (failure) { if (!requestController.signal.aborted) setSaveError(failure); }
    finally { sending.current = false; if (!requestController.signal.aborted) setSaving(false); }
  };
  return <><section id="public-name" ref={section} aria-labelledby="public-name-heading" className={classes.profileRow}>
    <div className={classes.profileIntro}>
      <Typography component="h2" id="public-name-heading" className={classes.profileTitle}>公開名</Typography>
      <Typography className={classes.profileLead}>公開名が未登録の場合、主催者は代表者名、講師は指導者名を初期登録します。兼任時は代表者名を優先します。その他の場合は空欄から設定できます。ここで変更すると過去の記事にも反映されます。</Typography>
    </div>
    <div className={classes.profileControls}>
    {loading && <Typography role="status" className={classes.message}>{initializing ? '公開名を初期登録しています。' : '公開名を確認しています。'}</Typography>}
    {error && <div role="alert"><Typography className={classes.message}>{initializationFailed ? '公開名の初期登録結果を確認できませんでした。公開名を再読み込みして確認してください。' : publicNameErrorMessage(error)}</Typography><Button type="button" onClick={reload} disabled={saving || loading}>公開名を再読み込み</Button></div>}
    {profile && !profile.can_edit_public_name && <Typography role="alert" className={classes.message}>公開名を変更する資格がありません。</Typography>}
    {profile?.can_edit_public_name && <form noValidate onSubmit={save} aria-label="公開名の設定" aria-busy={saving || loading}>
      <Typography className={classes.message}>登録済みの公開名：{profile.display_name || '未登録'}</Typography>
      <TextField id="public-name-input" className={classes.field} label="公開名" value={name} variant="outlined" size="small" fullWidth required
        autoComplete="off" InputLabelProps={{ shrink: true }} disabled={saving || loading} error={Boolean(saveError?.kind === 'validation')}
        helperText={saveError?.kind === 'validation' ? '公開名を1〜255文字で入力してください。' : '初期登録された公開名も変更できます。未登録の会員は空欄から設定してください。'}
        onChange={(event) => { setDraftName(event.target.value); setMessage(''); setSaveError(null); }} />
      <div><button className={classes.saveButton} type="submit" disabled={saving || loading || initializationFailed}>公開名を保存</button></div>
      {saving && <Typography role="status" className={classes.message}>保存しています。</Typography>}
      {saveError && <Typography role="alert" className={classes.message}>{publicNameErrorMessage(saveError, true)}</Typography>}
      {saveError && !['validation', 'forbidden'].includes(saveError.kind) && <div className={classes.actions}><Button type="button" onClick={reload} disabled={loading || saving}>公開名を再読み込み</Button></div>}
      {message && <Typography role="status" className={classes.message}>{message}</Typography>}
    </form>}
    </div>
  </section><hr /></>;
}

export default function PublicNameSettings({ layoutReady = true }) {
  const auth = useContext(AuthContext) || {};
  const policy = useBlogReleasePolicy();
  const scope = accountScope(auth);
  const permitted = policy.public_release || policy.can_manage;
  const mountedScope = useRef(null);
  if (permitted && auth.isAuth) mountedScope.current = scope;
  if (!auth.isAuth || mountedScope.current !== scope) return null;
  return <div hidden={!permitted}><PublicNameForm key={scope} layoutReady={layoutReady} enabled={permitted} /></div>;
}
